import { mapError } from './errors.js';
import { loadRuntimeEnv } from './runtimeEnv.js';

const CLERK_SCRIPT_TIMEOUT_MS = 5000;
const CLERK_LOAD_TIMEOUT_MS = 10000;

export interface ClerkSessionLike {
  getToken: () => Promise<string | null>;
  user?: ClerkUserLike | null;
}

interface ClerkEmailAddressLike {
  emailAddress?: string | null;
}

interface ClerkUserLike {
  fullName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  username?: string | null;
  primaryEmailAddress?: ClerkEmailAddressLike | null;
  emailAddresses?: ClerkEmailAddressLike[] | null;
}

export interface ClerkLike {
  session: ClerkSessionLike | null;
  user?: ClerkUserLike | null;
  load?: (options?: Record<string, unknown>) => Promise<void>;
  redirectToSignIn: (options?: { afterSignInUrl?: string }) => Promise<void>;
  signOut: () => Promise<void>;
}

function normalizeEmail(value?: string | null): string | null {
  if (!value) {
    return null;
  }

  const normalized = value.trim().toLowerCase();
  return normalized.length > 0 ? normalized : null;
}

function normalizeName(value?: string | null): string | null {
  if (!value) {
    return null;
  }

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}

export function extractClerkProfile(clerk: ClerkLike): { email: string | null; name: string | null } {
  const user = clerk.user ?? clerk.session?.user ?? null;
  if (!user) {
    return {
      email: null,
      name: null,
    };
  }

  const email =
    normalizeEmail(user.primaryEmailAddress?.emailAddress) ??
    normalizeEmail(user.emailAddresses?.find((entry) => normalizeEmail(entry.emailAddress))?.emailAddress) ??
    null;

  const fullName = normalizeName(user.fullName);
  const composedName =
    normalizeName([user.firstName, user.lastName].filter(Boolean).join(' ')) ??
    normalizeName(user.username) ??
    null;

  return {
    email,
    name: fullName ?? composedName,
  };
}

function withTimeout<T>(promise: Promise<T>, timeoutMs: number, label: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => {
      reject(new Error(`${label} timed out after ${Math.round(timeoutMs / 1000)}s`));
    }, timeoutMs);

    promise
      .then((value) => {
        window.clearTimeout(timer);
        resolve(value);
      })
      .catch((error: unknown) => {
        window.clearTimeout(timer);
        reject(error);
      });
  });
}

function decodeFrontendApiHostFromPublishableKey(publishableKey: string): string | null {
  const segments = publishableKey.split('_');
  const encodedHost = segments[2];
  if (!encodedHost) {
    return null;
  }

  let normalized = encodedHost.replace(/-/g, '+').replace(/_/g, '/');
  while (normalized.length % 4 !== 0) {
    normalized += '=';
  }

  try {
    const decoded = atob(normalized).trim();
    if (!decoded) {
      return null;
    }
    return decoded.endsWith('$') ? decoded.slice(0, -1) : decoded;
  } catch {
    return null;
  }
}

function clerkScriptSources(publishableKey: string): string[] {
  const sources: string[] = [];
  const host = decodeFrontendApiHostFromPublishableKey(publishableKey);

  if (host) {
    sources.push(`https://${host}/npm/@clerk/clerk-js@5/dist/clerk.browser.js`);
  }

  sources.push('https://cdn.jsdelivr.net/npm/@clerk/clerk-js@5/dist/clerk.browser.js');
  return [...new Set(sources)];
}

async function loadClerkScript(publishableKey: string): Promise<void> {
  const sources = clerkScriptSources(publishableKey);
  const errors: string[] = [];

  for (const source of sources) {
    try {
      await new Promise<void>((resolve, reject) => {
        const script = document.createElement('script');
        let settled = false;

        const finish = (error?: Error) => {
          if (settled) {
            return;
          }
          settled = true;
          window.clearTimeout(timer);
          script.onload = null;
          script.onerror = null;
          if (error) {
            script.remove();
            reject(error);
            return;
          }
          resolve();
        };

        const timer = window.setTimeout(() => {
          finish(new Error(`Timed out loading Clerk script: ${source}`));
        }, CLERK_SCRIPT_TIMEOUT_MS);

        script.src = source;
        script.async = true;
        script.crossOrigin = 'anonymous';
        script.type = 'text/javascript';
        script.setAttribute('data-clerk-publishable-key', publishableKey);
        script.onload = () => finish();
        script.onerror = () => finish(new Error(`Failed to load Clerk script: ${source}`));
        document.head.appendChild(script);
      });
      return;
    } catch (error) {
      errors.push(mapError(error));
    }
  }

  throw new Error(`Unable to load Clerk browser SDK. Attempts: ${errors.join(' | ')}`);
}

export async function loadClerk(): Promise<ClerkLike> {
  const env = loadRuntimeEnv();
  const publishableKey = env.VITE_CLERK_PUBLISHABLE_KEY.trim();
  if (!publishableKey) {
    throw new Error('Missing VITE_CLERK_PUBLISHABLE_KEY in frontend runtime config');
  }
  if (!publishableKey.startsWith('pk_')) {
    throw new Error('VITE_CLERK_PUBLISHABLE_KEY is invalid. Expected value to start with pk_.');
  }

  window.CLERK_PUBLISHABLE_KEY = publishableKey;
  window.__clerk_publishable_key = publishableKey;

  if (!window.Clerk) {
    await loadClerkScript(publishableKey);
  }

  if (!window.Clerk) {
    throw new Error('Clerk SDK failed to initialize');
  }

  const clerkCandidate = window.Clerk as
    | ClerkLike
    | (new (publishableKey: string) => ClerkLike & { load: () => Promise<void> });

  if (typeof clerkCandidate === 'function') {
    const clerkInstance = new clerkCandidate(publishableKey);
    await withTimeout(clerkInstance.load(), CLERK_LOAD_TIMEOUT_MS, 'Clerk initialization');
    return clerkInstance;
  }

  if (typeof clerkCandidate.load !== 'function') {
    throw new Error('Clerk SDK loaded, but Clerk.load is unavailable.');
  }

  try {
    await withTimeout(
      clerkCandidate.load({
        publishableKey,
      }),
      CLERK_LOAD_TIMEOUT_MS,
      'Clerk initialization',
    );
  } catch (firstError) {
    try {
      await withTimeout(clerkCandidate.load(), CLERK_LOAD_TIMEOUT_MS, 'Clerk initialization');
    } catch (secondError) {
      throw new Error(
        `Clerk SDK failed to initialize. Option-load error: ${mapError(firstError)}. Fallback-load error: ${mapError(
          secondError,
        )}.`,
      );
    }
  }

  return clerkCandidate;
}
